import {
  copyText,
  WSButton,
  WSCard,
  WSFlexBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { InvoiceStatus } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { addNotification } from "../../components/Notification/Notification";
import { queriesInvoices, queriesPayers } from "@query";
import { selectorPayerDisplayName } from "../../shared/selectors/selectorPayerDisplayName";
import { AlertEnableTaxWithholdings } from "../../modules/TaxWithholdings/components/AlertEnableTaxWithholdings";
import { WSQueries } from "../../query/WSQuery";
import { useMutationDownloadInvoicePdf } from "../../query/payments/mutations";
import { Layout } from "../../shared/components/Layout";
import { openIntercom } from "../../shared/utils/intercom";
import { FirstInvoiceBanner } from "../components/FirstInvoiceBanner/FirstInvoiceBanner";
import {
  InvoiceStatusTimeline,
  InvoiceTimelineStatus
} from "../components/InvoiceStatusTimeline/InvoiceStatusTimeline";
import { InvoiceTotals } from "../components/InvoiceTotals/InvoiceTotals";
import { useInvoiceFormGoBack } from "../utils/invoicesFormUtils";

export const RouteInvoiceConfirmation: React.FC<
  RouteComponentProps<{
    invoiceId: string;
  }>
> = ({
  match: {
    params: { invoiceId }
  }
}) => {
  const history = useHistory();
  const queryInvoice = queriesInvoices.getInvoice.useQuery({ invoiceId });
  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: queryInvoice.data?.payerId! },
    {
      enabled: !!queryInvoice.data?.payerId,
      retry: false
    }
  );
  const [downloadInvoicePdf, downloadInvoicePdfMeta] =
    useMutationDownloadInvoicePdf();
  const [createInvoiceSecureLink, createInvoiceSecureLinkMeta] =
    queriesInvoices.createInvoiceSecureLink.useMutation();
  const copyInvoiceLink = () => {
    createInvoiceSecureLink(
      { invoiceId },
      {
        onSuccess: link => {
          if (link?.url) {
            copyText(link.url);
            addNotification({ text: "Invoice link copied to clipboard." });
          }
        }
      }
    );
  };
  const goBack = useInvoiceFormGoBack();

  return (
    <Layout
      onClose={goBack}
      title={
        queryInvoice.data
          ? `Invoice #${queryInvoice.data?.invoiceNumber}`
          : "Invoice"
      }
    >
      <WSQueries
        queries={{
          queryInvoice,
          queryPayer
        }}
      >
        {({ queryInvoice: { data: invoice }, queryPayer: { data: payer } }) => {
          const payerName = selectorPayerDisplayName(payer);

          return (
            <WSList gap="XL">
              <FirstInvoiceBanner />

              <WSCard>
                <WSFlexBox justify="space-between" wrap="nowrap">
                  <WSText.Heading4 mb="M">{payerName}</WSText.Heading4>
                  <WSButton.Link
                    onClick={() => {
                      downloadInvoicePdf({
                        invoiceId: invoice.id!
                      });
                    }}
                    loading={downloadInvoicePdfMeta.isLoading}
                  >
                    Download PDF
                  </WSButton.Link>
                </WSFlexBox>

                <WSText.Heading5 mb="XS">
                  {invoice.status === InvoiceStatus.PENDING
                    ? "Invoice is pending"
                    : invoice.notificationPreferences?.shouldSendInvoice
                      ? "Your invoice has been sent"
                      : "Your invoice has been opened"}
                </WSText.Heading5>
                <WSText color="gray500" mb="XL">
                  {/* TODO (v3-migration): payer.email is empty for active payers */}
                  <WSText>
                    {invoice.notificationPreferences?.shouldSendInvoice
                      ? invoice.status === InvoiceStatus.PENDING
                        ? `Will be sent to ${payer.email}`
                        : `Sent to ${payer.email}`
                      : "Use your invoice link to send your payment requests directly to clients through your existing communication channel."}
                  </WSText>
                  {invoice.status === InvoiceStatus.PENDING && (
                    <WSButton.Link my="M" onClick={openIntercom}>
                      Ask about pending invoices
                    </WSButton.Link>
                  )}
                </WSText>
                <InvoiceStatusTimeline
                  currentStatus={
                    invoice.status === InvoiceStatus.PENDING
                      ? undefined
                      : "Sent"
                  }
                  statuses={[
                    InvoiceTimelineStatus.Sent,
                    InvoiceTimelineStatus.Viewed,
                    InvoiceTimelineStatus.Paid,
                    InvoiceTimelineStatus.Deposited
                  ]}
                  mb="XL"
                />
                <WSButton
                  fullWidth
                  mr="M"
                  name="copyInvoiceLink"
                  kind="Secondary"
                  loading={createInvoiceSecureLinkMeta.isLoading}
                  onClick={copyInvoiceLink}
                >
                  Copy invoice link
                </WSButton>
              </WSCard>

              <WSCard>
                <WSText.Heading5 mb="M" data-testid="paymentTimingText">
                  Payment timing
                </WSText.Heading5>
                <WSText color="gray600">
                  Funds are automatically deposited within 7 business days of
                  payment.
                </WSText>
              </WSCard>

              <WSCard>
                <WSList gap="2XL">
                  <WSText.Heading5>Summary</WSText.Heading5>

                  <InvoiceTotals invoice={invoice} />

                  <AlertEnableTaxWithholdings />

                  <WSButton
                    onClick={() => {
                      history.push("/member/invoices");
                    }}
                    fullWidth
                  >
                    Back to invoices
                  </WSButton>
                </WSList>
              </WSCard>
            </WSList>
          );
        }}
      </WSQueries>
    </Layout>
  );
};
